import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';

const WhatsOnSection = styled.section`
  padding: 2rem 5rem;
  margin-bottom: 2rem;
  background: white;
  
  @media (max-width: 768px) {
    padding: 1.5rem 2rem;
    margin-bottom: 1.5rem;
  }
`;

const WhatsOnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    overflow: visible;
  }
`;

const ScrollContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 0 2rem;
  
  @media (max-width: 600px) {
    overflow: visible;
    padding: 0;
  }
`;

const ScrollContent = styled.div`
  display: flex;
  transition: transform 0.3s ease-in-out;
  width: 100%;
  
  @media (max-width: 600px) {
    display: block;
    transform: none !important;
  }
`;

const EventGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  flex: 0 0 100%;
  padding: 0 1rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  opacity: 0.8;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
  
  @media (max-width: 600px) {
    display: none;
  }
`;

const LeftButton = styled(NavigationButton)`
  left: 0;
`;

const RightButton = styled(NavigationButton)`
  right: 0;
`;

const EventBadges = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
`;

const IconBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: #f0f0f0;
  color: #666;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const FlagshipBadge = styled(IconBadge)`
  background: ${props => props.theme.colors.accent};
  color: white;
`;

const LocationBadge = styled.span`
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const EventCard = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  height: 100%;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
  cursor: pointer;
`;

const ClickableImage = styled.div`
  cursor: pointer;
  position: relative;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const EventImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
  
  @media (max-width: 600px) {
    height: 300px;
  }
`;

const ClickableTitle = styled.h3`
  color: ${props => props.theme.colors.accent};
  font-family: ${props => props.theme.fonts.main};
  margin: 1rem 0 0.5rem; 
  font-size: 1.5rem;
  cursor: pointer;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin: 0.75rem 0 0.5rem;
  }
`;

const EventDate = styled.p`
  color: #666;
  margin: 0 0 0.5rem;
  font-weight: bold;
  font-size: 0.9rem;
`;

const EventDescription = styled.p`
  color: #666;
  margin: 0 0 1rem;
  flex: 1;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const ReadMore = styled.button`
  color: ${props => props.theme.colors.accent};
  text-decoration: none;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: inherit;
  text-align: left;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.colors.accent};
  font-family: ${props => props.theme.fonts.main};
  text-align: left;
  margin-bottom: 2rem;
  font-size: 2.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const LocationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-top: 0.75rem;
`;

const Badge = styled.span`
  padding: 0.5rem 1rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  background: #f0f0f0;
  color: #666;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: capitalize;
`;

const EmptyMessage = styled.p`
  color: #666;
  font-size: 1.1rem;
  text-align: center;
  margin: 2rem 0;
  padding: 2rem;
  background: #f9f9f9;
  border-radius: 8px;
`;

const formatEventDate = (dateStr) => {
  if (!dateStr) return '';
  
  // Check if it's a date range
  if (dateStr.includes(' to ')) {
    const [startDate, endDate] = dateStr.split(' to ');
    
    // Extract day and month from start date
    const startParts = startDate.split(', ');
    const startDayMonth = startParts[1].split(' ');
    const startDay = startDayMonth[1];
    const startMonth = startDayMonth[0];
    
    // Extract day and month from end date
    const endParts = endDate.split(', ');
    const endDayMonth = endParts[1].split(' ');
    const endDay = endDayMonth[1];
    const endMonth = endDayMonth[0];
    const year = endParts[2];
    
    // If same month, show "12-13 July 2025"
    if (startMonth === endMonth) {
      return `${startDay}-${endDay} ${startMonth} ${year}`;
    }
    // If different months, show "12 July - 13 Aug 2025"
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
  }
  
  // For single dates, show "12 July 2025"
  const parts = dateStr.split(', ');
  if (parts.length >= 2) {
    const dayMonth = parts[1].split(' ');
    return `${dayMonth[1]} ${dayMonth[0]} ${parts[2]}`;
  }
  
  return dateStr;
};

const WhatsOn = ({ title, events, emptyMessage, showParklandSection = true, enableScrolling = false }) => {
  const navigate = useNavigate();
  const [mainCurrentPage, setMainCurrentPage] = React.useState(0);
  const [parklandCurrentPage, setParklandCurrentPage] = React.useState(0);
  const [visibleEvents, setVisibleEvents] = React.useState(4);
  
  React.useEffect(() => {
    const updateVisibleEvents = () => {
      if (window.innerWidth >= 1200) {
        setVisibleEvents(4);
      } else if (window.innerWidth >= 900) {
        setVisibleEvents(3);
      } else if (window.innerWidth >= 600) {
        setVisibleEvents(2);
      } else {
        setVisibleEvents(1);
      }
    };
    
    updateVisibleEvents();
    window.addEventListener('resize', updateVisibleEvents);
    return () => window.removeEventListener('resize', updateVisibleEvents);
  }, []);
  
  const handleEventClick = (eventId) => {
    if (eventId) {
      navigate(`/event/${eventId}`);
    } else {
      console.error("No event ID provided for navigation");
    }
  };

  const handlePrevClick = (isParkland = false) => {
    if (isParkland) {
      setParklandCurrentPage(prev => Math.max(0, prev - 1));
    } else {
      setMainCurrentPage(prev => Math.max(0, prev - 1));
    }
  };

  const handleNextClick = (isParkland = false) => {
    if (isParkland) {
      setParklandCurrentPage(prev => Math.min(Math.ceil(parklandEvents.length / visibleEvents) - 1, prev + 1));
    } else {
      setMainCurrentPage(prev => Math.min(Math.ceil(otherEvents.length / visibleEvents) - 1, prev + 1));
    }
  };

  // Filter events into two categories
  const parklandEvents = showParklandSection ? events.filter(event => {
    const location = event.location?.toLowerCase() || '';
    return location === 'parkland';
  }) : [];
  
  const otherEvents = events.filter(event => {
    const location = event.location?.toLowerCase() || '';
    return location !== 'parkland';
  });

  const renderEvents = (eventsToRender, isParkland = false) => {
    // If scrolling is disabled and it's not the main section, just show the first 4 events
    if (!enableScrolling && !isParkland) {
      return (
        <WhatsOnGrid>
          {eventsToRender.slice(0, 4).map((event, index) => (
            <EventCard key={index} onClick={() => handleEventClick(event.id)}>
              <ClickableImage onClick={(e) => {
                e.stopPropagation();
                handleEventClick(event.id);
              }}>
                <EventImage src={event.image} alt={event.title} />
              </ClickableImage>
              <ContentWrapper>
                <EventBadges>
                  <LocationBadge>{event.location}</LocationBadge>
                  {event.isFlagship && (
                    <FlagshipBadge>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
                      </svg>
                      Flagship Event
                    </FlagshipBadge>
                  )}
                  {event.isEarlyBird && (
                    <IconBadge>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                      Early Bird
                    </IconBadge>
                  )}
                  {event.isDogFriendly && (
                    <IconBadge>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M21,6h-2l-1.27-1.27C17.28,4.28,16.83,4,16.34,4h-2.68c-0.49,0-0.94,0.28-1.39,0.73L11,6H9 C6.24,6,4,8.24,4,11v7c0,2.76,2.24,5,5,5h6c2.76,0,5-2.24,5-5v-7c0-2.76-2.24-5-5-5h-2l1-1h2.34l1.27,1.27C19.72,7.72,20.17,8,20.66,8H21 c0.55,0,1-0.45,1-1S21.55,6,21,6z M16,18c0,1.65-1.35,3-3,3s-3-1.35-3-3s1.35-3,3-3S16,16.35,16,18z"/>
                      </svg>
                      Dog Friendly
                    </IconBadge>
                  )}
                  {event.isAccessible && (
                    <IconBadge>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12,2c1.1,0,2,0.9,2,2s-0.9,2-2,2s-2-0.9-2-2S10.9,2,12,2z M21,9h-6v13h-2v-6h-2v6H9V9H3V7h18V9z"/>
                      </svg>
                      Accessible
                    </IconBadge>
                  )}
                </EventBadges>
                <ClickableTitle onClick={(e) => {
                  e.stopPropagation();
                  handleEventClick(event.id);
                }}>
                  {event.title}
                </ClickableTitle>
                <EventDate>{formatEventDate(event.date)}</EventDate>
                <EventDescription>{event.description}</EventDescription>
                
                {event.eventTags && event.eventTags.length > 0 && (
                  <TagsContainer>
                    {event.eventTags.map((tag, tagIndex) => (
                      <Tag key={tagIndex}>{tag}</Tag>
                    ))}
                  </TagsContainer>
                )}
                
                <ReadMore onClick={(e) => {
                  e.stopPropagation();
                  handleEventClick(event.id);
                }}>
                  {event.linkText || 'Read More'}
                </ReadMore>
              </ContentWrapper>
            </EventCard>
          ))}
        </WhatsOnGrid>
      );
    }

    // On mobile, show all events in a single column
    if (window.innerWidth <= 600) {
      return (
        <WhatsOnGrid>
          {eventsToRender.map((event, index) => (
            <EventCard key={index} onClick={() => handleEventClick(event.id)}>
              <ClickableImage onClick={(e) => {
                e.stopPropagation();
                handleEventClick(event.id);
              }}>
                <EventImage src={event.image} alt={event.title} />
              </ClickableImage>
              <ContentWrapper>
                <EventBadges>
                  <LocationBadge>{event.location}</LocationBadge>
                  {event.isFlagship && (
                    <FlagshipBadge>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
                      </svg>
                      Flagship Event
                    </FlagshipBadge>
                  )}
                  {event.isEarlyBird && (
                    <IconBadge>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                      Early Bird
                    </IconBadge>
                  )}
                  {event.isDogFriendly && (
                    <IconBadge>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M21,6h-2l-1.27-1.27C17.28,4.28,16.83,4,16.34,4h-2.68c-0.49,0-0.94,0.28-1.39,0.73L11,6H9 C6.24,6,4,8.24,4,11v7c0,2.76,2.24,5,5,5h6c2.76,0,5-2.24,5-5v-7c0-2.76-2.24-5-5-5h-2l1-1h2.34l1.27,1.27C19.72,7.72,20.17,8,20.66,8H21 c0.55,0,1-0.45,1-1S21.55,6,21,6z M16,18c0,1.65-1.35,3-3,3s-3-1.35-3-3s1.35-3,3-3S16,16.35,16,18z"/>
                      </svg>
                      Dog Friendly
                    </IconBadge>
                  )}
                  {event.isAccessible && (
                    <IconBadge>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12,2c1.1,0,2,0.9,2,2s-0.9,2-2,2s-2-0.9-2-2S10.9,2,12,2z M21,9h-6v13h-2v-6h-2v6H9V9H3V7h18V9z"/>
                      </svg>
                      Accessible
                    </IconBadge>
                  )}
                </EventBadges>
                <ClickableTitle onClick={(e) => {
                  e.stopPropagation();
                  handleEventClick(event.id);
                }}>
                  {event.title}
                </ClickableTitle>
                <EventDate>{formatEventDate(event.date)}</EventDate>
                <EventDescription>{event.description}</EventDescription>
                
                {event.eventTags && event.eventTags.length > 0 && (
                  <TagsContainer>
                    {event.eventTags.map((tag, tagIndex) => (
                      <Tag key={tagIndex}>{tag}</Tag>
                    ))}
                  </TagsContainer>
                )}
                
                <ReadMore onClick={(e) => {
                  e.stopPropagation();
                  handleEventClick(event.id);
                }}>
                  {event.linkText || 'Read More'}
                </ReadMore>
              </ContentWrapper>
            </EventCard>
          ))}
        </WhatsOnGrid>
      );
    }

    // For desktop, use the existing scrolling functionality
    const eventGroups = [];
    for (let i = 0; i < eventsToRender.length; i += visibleEvents) {
      eventGroups.push(eventsToRender.slice(i, i + visibleEvents));
    }

    const currentPage = isParkland ? parklandCurrentPage : mainCurrentPage;

    return (
      <ScrollContainer>
        <ScrollContent style={{ transform: `translateX(-${currentPage * 100}%)` }}>
          {eventGroups.map((group, groupIndex) => (
            <EventGroup key={groupIndex}>
              {group.map((event, index) => (
                <EventCard key={index} onClick={() => handleEventClick(event.id)}>
                  <ClickableImage onClick={(e) => {
                    e.stopPropagation();
                    handleEventClick(event.id);
                  }}>
                    <EventImage src={event.image} alt={event.title} />
                  </ClickableImage>
                  <ContentWrapper>
                    <EventBadges>
                      <LocationBadge>{event.location}</LocationBadge>
                      {event.isFlagship && (
                        <FlagshipBadge>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
                          </svg>
                          Flagship Event
                        </FlagshipBadge>
                      )}
                      {event.isEarlyBird && (
                        <IconBadge>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                          </svg>
                          Early Bird
                        </IconBadge>
                      )}
                      {event.isDogFriendly && (
                        <IconBadge>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M21,6h-2l-1.27-1.27C17.28,4.28,16.83,4,16.34,4h-2.68c-0.49,0-0.94,0.28-1.39,0.73L11,6H9 C6.24,6,4,8.24,4,11v7c0,2.76,2.24,5,5,5h6c2.76,0,5-2.24,5-5v-7c0-2.76-2.24-5-5-5h-2l1-1h2.34l1.27,1.27C19.72,7.72,20.17,8,20.66,8H21 c0.55,0,1-0.45,1-1S21.55,6,21,6z M16,18c0,1.65-1.35,3-3,3s-3-1.35-3-3s1.35-3,3-3S16,16.35,16,18z"/>
                          </svg>
                          Dog Friendly
                        </IconBadge>
                      )}
                      {event.isAccessible && (
                        <IconBadge>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12,2c1.1,0,2,0.9,2,2s-0.9,2-2,2s-2-0.9-2-2S10.9,2,12,2z M21,9h-6v13h-2v-6h-2v6H9V9H3V7h18V9z"/>
                          </svg>
                          Accessible
                        </IconBadge>
                      )}
                    </EventBadges>
                    <ClickableTitle onClick={(e) => {
                      e.stopPropagation();
                      handleEventClick(event.id);
                    }}>
                      {event.title}
                    </ClickableTitle>
                    <EventDate>{formatEventDate(event.date)}</EventDate>
                    <EventDescription>{event.description}</EventDescription>
                    
                    {event.eventTags && event.eventTags.length > 0 && (
                      <TagsContainer>
                        {event.eventTags.map((tag, tagIndex) => (
                          <Tag key={tagIndex}>{tag}</Tag>
                        ))}
                      </TagsContainer>
                    )}
                    
                    <ReadMore onClick={(e) => {
                      e.stopPropagation();
                      handleEventClick(event.id);
                    }}>
                      {event.linkText || 'Read More'}
                    </ReadMore>
                  </ContentWrapper>
                </EventCard>
              ))}
            </EventGroup>
          ))}
        </ScrollContent>
        {eventGroups.length > 1 && (
          <>
            <LeftButton onClick={() => handlePrevClick(isParkland)} disabled={currentPage === 0}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </LeftButton>
            <RightButton onClick={() => handleNextClick(isParkland)} disabled={currentPage >= eventGroups.length - 1}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
              </svg>
            </RightButton>
          </>
        )}
      </ScrollContainer>
    );
  };

  return (
    <>
      <WhatsOnSection>
        <SectionTitle>{title}</SectionTitle>
        {otherEvents.length > 0 ? (
          renderEvents(otherEvents, false)
        ) : (
          <EmptyMessage>{emptyMessage || "No events currently scheduled. Check back soon!"}</EmptyMessage>
        )}
      </WhatsOnSection>

      {/* Parkland Events Section */}
      {showParklandSection && parklandEvents.length > 0 && (
        <WhatsOnSection>
          <SectionTitle>What's On in the Parkland</SectionTitle>
          {renderEvents(parklandEvents, true)}
        </WhatsOnSection>
      )}
    </>
  );
};

WhatsOn.propTypes = {
  title: PropTypes.string.isRequired,
  events: PropTypes.array.isRequired,
  emptyMessage: PropTypes.string,
  showParklandSection: PropTypes.bool,
  enableScrolling: PropTypes.bool
};

export default WhatsOn;
